import React from 'react'
import {
  FileSignature,
  IndianRupee,
  BarChart3,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  XCircle
} from 'lucide-react'
import contractReportsService from '../../../services/contractReportsService'
import {
  ReportShell,
  ReportSection,
  ReportTable,
  KpiGrid,
  EmptyState,
  useContractReport,
  toInputDate,
  formatMoney,
  formatMoneyCompact,
  formatNumber,
  formatPercent,
  primaryCurrency,
  contractColumns,
  withExportOnly,
  breakdownColumns,
  breakdownFooter,
  summarySection
} from './reportShared'

const DEFAULT_FILTERS = { asOf: toInputDate(), fromDate: '', toDate: '' }

const topContractCols = withExportOnly(
  [
    contractColumns.contract,
    contractColumns.party,
    contractColumns.type,
    contractColumns.status,
    contractColumns.value,
    contractColumns.start,
    contractColumns.end
  ],
  [contractColumns.title, contractColumns.currency]
)

const milestoneRows = (m) => [
  { status: 'Completed', count: m.completed },
  { status: 'In Progress', count: m.inProgress },
  { status: 'Pending', count: m.pending },
  { status: 'Delayed', count: m.delayed },
  { status: 'Cancelled', count: m.cancelled },
  { status: 'Overdue (open, past due date)', count: m.overdue },
  { status: 'No due date', count: m.withoutDate }
]

const milestoneCols = [
  { key: 'status', label: 'Milestone Status' },
  { key: 'count', label: 'Count', align: 'right', render: (r) => formatNumber(r.count) }
]

const ContractPerformanceReport = () => {
  const report = useContractReport(contractReportsService.getPerformanceReport, DEFAULT_FILTERS)

  const getExportSections = (data) => {
    const cur = primaryCurrency(data.meta)
    const s = data.summary
    return [
      summarySection('Summary', [
        ['Total contracts', s.totalContracts],
        ['Active', s.active],
        ['Expired', s.expired],
        ['Terminated', s.terminated],
        ['Suspended', s.suspended],
        ['Renewed', s.renewed],
        ['Cancelled', s.cancelled],
        ['Active contract value', s.totalContractValue],
        ['Total value (all statuses)', s.totalValueAllStatuses],
        ['Average contract value', s.avgContractValue],
        ['Expiring in 30 days', data.expiring.in30],
        ['Expiring in 60 days', data.expiring.in60],
        ['Expiring in 90 days', data.expiring.in90],
        ['Currencies', (data.meta.currencies || []).join(', ')]
      ]),
      { title: 'By Status', columns: breakdownColumns('status', 'Status', s.byStatus, cur), rows: s.byStatus },
      { title: 'By Type', columns: breakdownColumns('type', 'Contract Type', s.byType, cur), rows: s.byType },
      { title: 'By Party Type', columns: breakdownColumns('partyType', 'Party Type', s.byPartyType, cur), rows: s.byPartyType },
      { title: 'Drafts', columns: breakdownColumns('status', 'Draft Status', data.drafts.byStatus, cur), rows: data.drafts.byStatus },
      { title: 'Milestones', columns: milestoneCols, rows: milestoneRows(data.milestoneSummary) },
      { title: 'Top Contracts', columns: topContractCols.export, rows: data.topContracts }
    ]
  }

  return (
    <ReportShell
      title="Contract Performance"
      subtitle="Contract portfolio, value and delivery performance"
      path="/contracts/reports/performance"
      report={report}
      filterFields={['asOf', 'fromDate', 'toDate']}
      filterHint="Period filters signed contracts by signed date and drafts by draft date"
      exportName="Contract_Performance"
      getExportSections={getExportSections}
    >
      {(data) => {
        const s = data.summary
        const cur = primaryCurrency(data.meta)
        const m = data.milestoneSummary
        if (s.totalContracts === 0 && data.drafts.total === 0) {
          return <EmptyState message="No signed or draft contracts found for the selected period" />
        }
        return (
          <>
            <KpiGrid
              items={[
                {
                  icon: FileSignature,
                  label: 'Total Contracts',
                  value: formatNumber(s.totalContracts),
                  subtitle: `${formatNumber(s.active)} active · ${formatNumber(s.renewed)} renewed`
                },
                {
                  icon: IndianRupee,
                  label: 'Active Contract Value',
                  value: formatMoneyCompact(s.totalContractValue, cur),
                  title: formatMoney(s.totalContractValue, cur),
                  subtitle: `All statuses: ${formatMoneyCompact(s.totalValueAllStatuses, cur)}`
                },
                {
                  icon: BarChart3,
                  label: 'Avg Contract Value',
                  value: formatMoneyCompact(s.avgContractValue, cur),
                  title: formatMoney(s.avgContractValue, cur),
                  subtitle: `Active avg: ${formatMoneyCompact(s.avgActiveContractValue, cur)}`
                },
                {
                  icon: AlertTriangle,
                  label: 'Expiring in 30 Days',
                  value: formatNumber(data.expiring.in30),
                  badge: `60d: ${data.expiring.in60} · 90d: ${data.expiring.in90}`,
                  badgeTone: data.expiring.in30 > 0 ? 'negative' : 'neutral',
                  subtitle: 'Active contracts by end date'
                }
              ]}
            />
            <KpiGrid
              items={[
                {
                  icon: CheckCircle,
                  label: 'Milestone Completion',
                  value: formatPercent(m.completionRate),
                  subtitle: `${formatNumber(m.completed)} of ${formatNumber(m.total)} milestones`
                },
                {
                  icon: Clock,
                  label: 'Overdue Milestones',
                  value: formatNumber(m.overdue),
                  subtitle: `${formatNumber(m.delayed)} marked delayed`
                },
                {
                  icon: FileText,
                  label: 'Draft Contracts',
                  value: formatNumber(data.drafts.total),
                  subtitle: 'In the drafting pipeline'
                },
                {
                  icon: XCircle,
                  label: 'Ended Early',
                  value: formatNumber(s.terminated + s.cancelled),
                  subtitle: `${formatNumber(s.terminated)} terminated · ${formatNumber(s.cancelled)} cancelled · ${formatNumber(s.suspended)} suspended`
                }
              ]}
            />

            <div className="contract-report-grid-2">
              <ReportSection title="By Status" subtitle="Count and value of signed contracts">
                <ReportTable
                  columns={breakdownColumns('status', 'Status', s.byStatus, cur, { badge: true })}
                  rows={s.byStatus}
                  footer={breakdownFooter('status', s.byStatus, cur)}
                />
              </ReportSection>
              <ReportSection title="By Contract Type">
                <ReportTable
                  columns={breakdownColumns('type', 'Contract Type', s.byType, cur)}
                  rows={s.byType}
                  footer={breakdownFooter('type', s.byType, cur)}
                />
              </ReportSection>
            </div>

            <div className="contract-report-grid-2">
              <ReportSection title="By Party Type">
                <ReportTable
                  columns={breakdownColumns('partyType', 'Party Type', s.byPartyType, cur)}
                  rows={s.byPartyType}
                  footer={breakdownFooter('partyType', s.byPartyType, cur)}
                />
              </ReportSection>
              <ReportSection title="Draft Contracts" subtitle="Drafts by workflow status" count={`${data.drafts.total} drafts`}>
                <ReportTable
                  columns={breakdownColumns('status', 'Draft Status', data.drafts.byStatus, cur, { badge: true })}
                  rows={data.drafts.byStatus}
                  emptyText="No draft contracts"
                />
              </ReportSection>
            </div>

            <ReportSection
              title="Milestones"
              subtitle={`Across all contracts in scope · overdue = not completed/cancelled and due before ${new Date(data.meta.asOf).toLocaleDateString('en-IN')}`}
              count={`${formatNumber(m.total)} milestones`}
            >
              <ReportTable
                columns={milestoneCols}
                rows={m.total ? milestoneRows(m) : []}
                emptyText="No milestones recorded on contracts"
              />
            </ReportSection>

            <ReportSection title="Top Contracts by Value" subtitle="Top 15 signed contracts" count={`${data.topContracts.length} shown`}>
              <ReportTable columns={topContractCols.display} rows={data.topContracts} emptyText="No signed contracts" />
            </ReportSection>
          </>
        )
      }}
    </ReportShell>
  )
}

export default ContractPerformanceReport
